// components/profile/Activity.jsx
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  Trash2,
  PlusCircle,
  Gauge,
  CalendarDays,
  Loader2,
  Tag,
  AlignLeft,
  Eye,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  fetchActivities,
  createActivityFromGPX,
  createActivityFromRoute,
  deleteActivity as deleteActivityThunk,
} from "@/store/slices/activitySlice";
import { fetchRoutes } from "@/store/slices/routesSlice";
import { INDIAN_STATES } from "../../utils/constants";
import Stepper, { Step } from "../common/Stepper";
import Modal from "../common/Modal";
import TextInput from "../common/TextInput";
import TextArea from "../common/TextArea";
import SelectDropdown from "../common/SelectDropdown";
import Card from "../common/Card";
import Button from "../common/Button";
import GPXUpload from "../common/GPXUpload";
import DatePicker from "../common/DatePicker";
import ActivityViewModal from "./ActivityViewModal";

const inputClass =
  "w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500";

const Activity = () => {
  const dispatch = useDispatch();
  const { activities, loading, submitLoading, error } = useSelector(
    (state) => state.activity
  );
  const { routes } = useSelector((state) => state.routes);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityMode, setActivityMode] = useState("gpx"); // 'gpx' | 'route'
  const [gpxFile, setGpxFile] = useState(null);
  const [selectedState, setSelectedState] = useState("");
  const [selectedRoute, setSelectedRoute] = useState(null);

  const stepperRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    duration: "",
    completed_at: null,
  });

  // Fetch activities on mount
  useEffect(() => {
    dispatch(fetchActivities());
  }, [dispatch]);

  // Reset form & stepper
  const resetAll = () => {
    setForm({
      title: "",
      duration: "",
      completed_at: null,
    });
    setGpxFile(null);
    setSelectedState("");
    setSelectedRoute(null);
    setActivityMode("gpx");
    if (stepperRef.current?.reset) stepperRef.current.reset();
  };

  // Close modal and reset
  const handleCloseModal = () => {
    setDialogOpen(false);
    resetAll();
  };

  // Handle GPX file selection
  const handleGPXUpload = (file) => {
    setGpxFile(file);
  };

  // Submit activity with GPX file or existing route
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || !form.duration || !form.completed_at) {
      toast.warn("Please fill all required fields.");
      return;
    }

    if (activityMode === "gpx") {
      // GPX Upload Mode
      if (!gpxFile) {
        toast.warn("Please upload a GPX file.");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("gpxFile", gpxFile);
        formData.append("title", form.title.trim());
        formData.append("duration", Number(form.duration));
        if (form.completed_at) {
          formData.append("completed_at", form.completed_at.toISOString());
        }

        const result = await dispatch(createActivityFromGPX(formData)).unwrap();
        toast.success(result.message || "Activity added successfully!");
        handleCloseModal();
      } catch (err) {
        toast.error(err || "Failed to add activity.");
      }
    } else {
      // Existing Route Mode
      if (!selectedRoute) {
        toast.warn("Please select a route.");
        return;
      }

      try {
        const payload = {
          route_id: selectedRoute.id,
          title: form.title.trim(),
          distance: selectedRoute.distance,
          duration: Number(form.duration),
        };
        if (form.completed_at) {
          payload.completed_at = form.completed_at.toISOString();
        }

        const result = await dispatch(
          createActivityFromRoute(payload)
        ).unwrap();
        toast.success(result.message || "Activity logged successfully!");
        handleCloseModal();
      } catch (err) {
        toast.error(err || "Failed to log activity.");
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this activity?"))
      return;
    try {
      await dispatch(deleteActivityThunk(id)).unwrap();
      toast.success("Activity deleted successfully.");
    } catch (err) {
      toast.error(err || "Failed to delete activity.");
    }
  };

  // Open modal, then mount Stepper
  const openModalAndMount = () => {
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <Loader2 className="animate-spin w-6 h-6 mb-2" />
        <p>Loading activities...</p>
      </div>
    );
  }

  return (
    <section className="space-y-10">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            Activity
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Track your rides and route performance
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={openModalAndMount}
            className="flex items-center gap-2"
          >
            <PlusCircle className="w-5 h-5" />
            Add Activity
          </Button>
        </div>
      </div>

      {/* Modal */}
      <Modal
        open={dialogOpen}
        onClose={handleCloseModal}
        title="Log New Activity"
      >
        <div className="max-h-[60vh] overflow-y-auto px-1">
          <form onSubmit={handleSubmit} className="mt-4">
            <Stepper ref={stepperRef} initialStep={1} className="pt-2">
              {/* Step 1: Basic Info + Upload/Route Selection */}
              <Step>
                <div className="space-y-4">
                  {/* Mode Toggle */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Activity Type *
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setActivityMode("gpx")}
                        className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                          activityMode === "gpx"
                            ? "bg-green-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                        }`}
                      >
                        Upload GPX
                      </button>
                      <button
                        type="button"
                        onClick={() => setActivityMode("route")}
                        className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                          activityMode === "route"
                            ? "bg-green-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                        }`}
                      >
                        Select Existing Route
                      </button>
                    </div>
                  </div>

                  <TextInput
                    label="Activity Title"
                    value={form.title}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, title: e.target.value }))
                    }
                    placeholder="Morning Ride, Evening Sprint..."
                    required
                  />

                  {/* Conditional: GPX Upload Mode */}
                  {activityMode === "gpx" && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        GPX File Upload *
                      </label>
                      <GPXUpload onUpload={handleGPXUpload} />
                      {gpxFile && (
                        <p className="text-sm text-green-600 font-medium dark:text-green-400">
                          ✓ GPX file selected: {gpxFile.name}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Conditional: Existing Route Mode */}
                  {activityMode === "route" && (
                    <>
                      <SelectDropdown
                        label="Select State *"
                        options={INDIAN_STATES.filter((s) => s.code).map(
                          (s) => ({
                            value: s.code,
                            label: s.name,
                          })
                        )}
                        value={selectedState}
                        onChange={(e) => {
                          setSelectedState(e.target.value);
                          setSelectedRoute(null); // Reset route when state changes
                          if (e.target.value) {
                            dispatch(fetchRoutes({ state: e.target.value }));
                          }
                        }}
                        placeholder="Select a State"
                      />

                      {selectedState && (
                        <SelectDropdown
                          label="Select Route *"
                          options={routes.map((r) => ({
                            value: r.id,
                            label: `${r.name} (${r.distance.toFixed(1)} km, ${r.difficulty})`,
                          }))}
                          value={selectedRoute?.id || ""}
                          onChange={(e) => {
                            const route = routes.find(
                              (r) => r.id === e.target.value
                            );
                            setSelectedRoute(route || null);
                          }}
                          placeholder="Select a Route"
                        />
                      )}

                      {selectedRoute && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md dark:bg-blue-900/10 dark:border-blue-800">
                          <p className="text-sm text-blue-800 dark:text-blue-300">
                            <span className="font-medium">Distance:</span>{" "}
                            {selectedRoute.distance.toFixed(2)} km
                          </p>
                          <p className="text-sm text-blue-800 dark:text-blue-300">
                            <span className="font-medium">Difficulty:</span>{" "}
                            {selectedRoute.difficulty}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  <TextInput
                    label="Duration (minutes) *"
                    type="number"
                    min="1"
                    value={form.duration}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, duration: e.target.value }))
                    }
                    required
                    placeholder="How long did it take?"
                  />

                  <div className="flex flex-col">
                    <DatePicker
                      label="Completed At *"
                      value={
                        form.completed_at
                          ? new Date(form.completed_at)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                      onChange={(e) => {
                        const dateStr = e.target.value;
                        if (dateStr) {
                          // Convert YYYY-MM-DD to Date object
                          setForm((p) => ({
                            ...p,
                            completed_at: new Date(dateStr),
                          }));
                        } else {
                          setForm((p) => ({ ...p, completed_at: null }));
                        }
                      }}
                      allowFuture={false}
                    />
                  </div>
                </div>
              </Step>

              {/* Step 3: Confirmation */}
              <Step>
                <div className="space-y-4">
                  <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-4 dark:bg-gray-800 dark:border-gray-700">
                    <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                      Confirm activity details
                    </h4>

                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-start gap-3">
                        <div className="text-green-600 p-2 bg-green-50 rounded-md dark:bg-green-900/20 dark:text-green-400">
                          <Tag className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Title
                          </div>
                          <div className="font-medium text-gray-800 dark:text-gray-200">
                            {form.title}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="text-purple-600 p-2 bg-purple-50 rounded-md dark:bg-purple-900/20 dark:text-purple-400">
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Duration
                          </div>
                          <div className="text-gray-800 dark:text-gray-200">
                            {form.duration ? `${form.duration} min` : "—"}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="text-gray-600 p-2 bg-gray-50 rounded-md dark:bg-gray-700 dark:text-gray-300">
                          <CalendarDays className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Completed At
                          </div>
                          <div className="text-gray-800 dark:text-gray-200">
                            {form.completed_at
                              ? new Date(form.completed_at).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  }
                                )
                              : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        if (stepperRef.current?.goTo)
                          stepperRef.current.goTo(1);
                      }}
                    >
                      Edit
                    </Button>

                    <Button
                      type="submit"
                      loading={submitLoading}
                      disabled={submitLoading}
                    >
                      {submitLoading ? "Adding..." : "Add Activity"}
                    </Button>
                  </div>
                </div>
              </Step>
            </Stepper>
          </form>
        </div>
      </Modal>

      {/* Activity list */}
      {activities.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <p>No activities logged yet.</p>
          <p className="text-sm mt-1">Start adding logs today!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.map((activity) => (
            <motion.div
              key={activity._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Card className="hover:shadow-md transition-shadow dark:bg-gray-800 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full">
                  <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full text-green-600 dark:text-green-400 shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                      {activity.title ||
                        activity.route_id?.name ||
                        "Unnamed Activity"}
                    </h3>
                    {activity.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                        {activity.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Gauge className="w-4 h-4" /> {activity.distance} km
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {activity.duration} min
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-4 h-4" />{" "}
                        {new Date(activity.completed_at).toLocaleDateString(
                          "en-IN",
                          { day: "2-digit", month: "short", year: "numeric" }
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Actions - Pushed to right on desktop */}
                  <div className="flex gap-2 sm:ml-auto mt-3 sm:mt-0 items-center justify-end w-full sm:w-auto">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setSelectedActivity(activity);
                          setViewModalOpen(true);
                        }}
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Activity View Modal */}
      <ActivityViewModal
        activity={selectedActivity}
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedActivity(null);
        }}
      />
    </section>
  );
};

export default Activity;
